import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Check, X, Users, Target, 
  Lightning, Handshake, ChartLineUp, Crown,
  CurrencyDollar, ShieldCheck, Star, Cross, BookOpen
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InnerCircleSalesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  useEffect(() => {
    if (isAuthenticated && user?.role === 'paid_member') {
      toast.info('You already have Inner Circle access!');
      navigate('/community', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.info('Please sign up or log in first to join the Inner Circle');
      navigate('/signup');
      return;
    }
    
    setLoading(true);
    try {
      const packageId = selectedPlan === 'monthly' ? 'inner_circle_monthly' : 'inner_circle_lifetime';
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin
      }, {
        headers: getAuthHeader()
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    
    if (isAuthenticated) {
      await handleCheckout();
      return;
    }
    
    if (!email || !name) {
      toast.error('Please enter your name and email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/funnel/inner-circle-interest`, { name, email, plan: selectedPlan });
      toast.success('Thank you! Please sign up to complete your purchase.');
      navigate('/signup');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F7F7F5]">
      {/* Navigation */}
      <nav className="nav-glass fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <Link to={user ? "/community" : "/"} className="text-2xl font-extrabold tracking-tighter text-[#C4A661]">POWR</Link>
          <div className="flex items-center gap-6">
            <Link to="/login" className="text-sm text-[#A3A3A3] hover:text-[#F7F7F5] transition-colors uppercase tracking-widest">
              Sign In
            </Link>
            <Button asChild className="btn-gold h-10 px-6 text-sm">
              <a href="#join">Join Now</a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Black Man Image */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 overflow-hidden">
        {/* Background Image - Black Man Success */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1621973133111-63649acea55a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920"
            alt="Black man celebrating success"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/70 to-[#0A0A0A]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/60 to-[#0A0A0A]/90" />
        </div>
        
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="text-center"
          >
            {/* Premium Upgrade Badge */}
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-[#C4A661]/10 border border-[#C4A661]/40 rounded-full mb-6">
              <Crown size={18} className="text-[#C4A661]" weight="fill" />
              <span className="text-[#C4A661] font-bold text-sm uppercase tracking-wider">Premium Upgrade</span>
            </div>
            
            <p className="text-overline mb-6">The Inner Circle</p>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] mb-8 uppercase">
              Take Your Brotherhood
              <br />
              <span className="text-[#C4A661]">To The Next Level.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl mx-auto mb-4 leading-relaxed">
              Already a free member? Unlock the full power of POWR with exclusive training, 
              mastermind access, and 1-on-1 accountability.
            </p>
            
            <p className="text-sm text-[#525252] mb-8">
              Free membership is great. Inner Circle is <span className="text-[#C4A661]">transformational</span>.
            </p>

            <p className="text-quote text-xl md:text-2xl mb-12">
              "The plans of the diligent lead surely to abundance."
              <span className="block text-sm text-[#A3A3A3] mt-2 not-italic font-sans tracking-widest">— Proverbs 21:5</span>
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild className="btn-gold h-14 px-10 text-lg" data-testid="hero-cta-btn">
                <a href="#join">
                  Upgrade to Inner Circle
                  <ArrowRight size={20} className="ml-2" weight="bold" />
                </a>
              </Button>
              <p className="text-sm text-[#A3A3A3]">30-Day Money-Back Guarantee</p>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="grid grid-cols-3 gap-6 mt-20 max-w-2xl mx-auto"
          >
            {[
              { value: '3', label: 'Income Systems' },
              { value: '24/7', label: 'Brotherhood' },
              { value: '100%', label: 'Faith-Driven' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl md:text-4xl font-extrabold text-[#C4A661]">{stat.value}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-widest mt-2">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Scripture Banner */}
      <section className="bg-[#C4A661] py-5">
        <div className="container mx-auto px-6 md:px-12 text-center">
          <p className="text-[#0A0A0A] font-medium">
            <Cross size={18} className="inline-block mr-2 -mt-1" weight="bold" />
            "Whatever you do, work heartily, as for the Lord and not for men." — Colossians 3:23
          </p>
        </div>
      </section>

      {/* Free vs Inner Circle Comparison */}
      <section className="section-dark section-padding">
        <div className="container mx-auto max-w-5xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <p className="text-overline mb-6">Membership Comparison</p>
            <h2 className="text-heading-lg mb-4">
              Free is <span className="text-[#10B981]">Great</span>. Inner Circle is <span className="text-[#C4A661]">Transformational</span>.
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">
              POWR membership is always free. But for brothers ready to go deeper, Inner Circle unlocks everything.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Membership */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#141414] border border-[#262626] rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#10B981]/20 flex items-center justify-center">
                  <Check size={24} className="text-[#10B981]" weight="bold" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#F7F7F5]">Free Membership</h3>
                  <p className="text-[#10B981] font-bold">$0 Forever</p>
                </div>
              </div>
              
              <ul className="space-y-4">
                {[
                  'Community Feed access',
                  'Daily devotionals',
                  'Free training courses',
                  'Brotherhood connections',
                  'Resource library',
                  'Mobile access'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A3A3A3]">
                    <Check size={16} className="text-[#10B981]" weight="bold" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 pt-6 border-t border-[#262626]">
                <Link to="/signup">
                  <Button className="w-full bg-[#262626] hover:bg-[#363636] text-[#F7F7F5] h-12">
                    Join Free Forever
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Inner Circle */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#C4A661]/10 to-[#0A0A0A] border-2 border-[#C4A661] rounded-2xl p-8 relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 px-3 py-1 bg-[#C4A661] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider rounded-full">
                Most Popular
              </div>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-[#C4A661]/20 flex items-center justify-center">
                  <Crown size={24} className="text-[#C4A661]" weight="fill" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#F7F7F5]">Inner Circle</h3>
                  <p className="text-[#C4A661] font-bold">$97/month</p>
                </div>
              </div>
              
              <ul className="space-y-4">
                {[
                  { text: 'Everything in Free', highlight: false },
                  { text: 'Premium mastermind courses', highlight: true },
                  { text: 'Weekly live calls', highlight: true },
                  { text: '1-on-1 accountability partner', highlight: true },
                  { text: 'Private Inner Circle community', highlight: true },
                  { text: 'AI wealth strategy tools', highlight: true },
                  { text: 'Direct access to mentors', highlight: true }
                ].map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 ${item.highlight ? 'text-[#F7F7F5]' : 'text-[#A3A3A3]'}`}>
                    <Check size={16} className="text-[#C4A661]" weight="bold" />
                    <span>{item.text}</span>
                    {item.highlight && <Star size={12} className="text-[#C4A661]" weight="fill" />}
                  </li>
                ))}
              </ul>
              
              <div className="mt-8 pt-6 border-t border-[#C4A661]/30">
                <Button asChild className="w-full btn-gold h-12">
                  <a href="#join">
                    Upgrade to Inner Circle
                    <ArrowRight size={18} className="ml-2" />
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Opening Copy - Light Section */}
      <section className="section-light section-padding">
        <div className="container mx-auto max-w-3xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="space-y-8"
          >
            <div className="w-12 h-px bg-[#C4A661] mx-auto mb-10" />
            
            <p className="text-2xl md:text-3xl font-bold text-center text-[#0A0A0A]">
              Here's the truth most won't tell you:
            </p>
            
            <div className="space-y-6 text-lg text-[#525252] leading-relaxed text-center">
              <p>
                Information is everywhere. Courses are everywhere. But most men are still stuck 
                in the same place they were a year ago.
              </p>
              <p>
                Why? Because they're in <span className="text-[#0A0A0A] font-semibold">learning mode</span>. 
                Watching videos. Taking notes. Feeling productive. 
                <span className="text-[#C4A661] font-semibold"> But never executing.</span>
              </p>
              <p>
                God didn't call you to consume content. He called you to 
                <span className="text-[#0A0A0A] font-semibold"> build something</span>.
              </p>
            </div>

            <p className="text-quote text-2xl text-center pt-6">
              "Faith without works is dead."
              <span className="block text-sm text-[#A3A3A3] mt-2 not-italic font-sans tracking-widest">— James 2:26</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Get - Dark Section */}
      <section className="section-dark section-padding">
        <div className="container mx-auto max-w-5xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-overline mb-6">What You Get</p>
            <h2 className="text-heading-lg">
              The Inner Circle <span className="text-[#C4A661]">Delivers:</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Target,
                title: 'Divine Direction',
                desc: 'A clear path aligned with your calling. No guessing. No wandering. Just purposeful execution toward God-given goals.'
              },
              {
                icon: Users,
                title: 'Brotherhood in Christ',
                desc: 'A tight-knit group of Black men who pray together, build together, and hold each other accountable in faith and business.'
              },
              {
                icon: ShieldCheck,
                title: 'Iron Sharpens Iron',
                desc: 'Weekly accountability. Clear milestones. Brothers who will challenge you, encourage you, and pray for you.'
              },
              {
                icon: Lightning,
                title: 'Proven Systems',
                desc: 'Biblical wealth-building strategies you can implement immediately. Not theory—executable frameworks rooted in scripture.'
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-premium p-8 group"
              >
                <div className="w-14 h-14 bg-[#C4A661]/10 border border-[#C4A661]/30 flex items-center justify-center mb-6 group-hover:bg-[#C4A661]/20 transition-colors">
                  <item.icon size={28} className="text-[#C4A661]" weight="duotone" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-[#F7F7F5]">{item.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Income Systems - Light Section with Black Men Image */}
      <section className="section-light section-padding">
        <div className="container mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-overline mb-6">The Systems</p>
              <h2 className="text-heading-lg text-[#0A0A0A] mb-8">
                Kingdom <span className="text-[#C4A661]">Income Systems</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  {
                    title: 'AffiliateAI Income System',
                    features: ['Niche selection that aligns with your values', 'AI-powered content creation', 'Traffic strategies that work', 'Converting clicks into income']
                  },
                  {
                    title: 'Digital Identity Builder',
                    features: ['Build your professional presence', 'Position yourself as a leader', 'Generate inbound opportunities', 'Turn your identity into impact']
                  },
                  {
                    title: 'EchoCard Networking',
                    features: ['Create connections that matter', 'Networking with purpose', 'Turn relationships into results', 'Expand your sphere of influence']
                  }
                ].map((system, i) => (
                  <div key={i} className="bg-[#F5F5F0] border border-[#E5E5E5] p-6">
                    <h3 className="text-lg font-bold text-[#0A0A0A] mb-4">{system.title}</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {system.features.map((feature, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <Check size={16} className="text-[#C4A661] flex-shrink-0" weight="bold" />
                          <span className="text-sm text-[#525252]">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1754577060078-21315dd188c8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
                alt="Black businessmen discussing strategy"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 border border-[#C4A661]/30" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Possible - Dark Section */}
      <section className="section-dark section-padding">
        <div className="container mx-auto max-w-5xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-overline mb-6">What's Possible</p>
            <h2 className="text-heading-lg">
              When You <span className="text-[#C4A661]">Execute:</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { milestone: 'First $100', desc: 'Your first commission. Proof that faith and action produce results.', icon: CurrencyDollar },
              { milestone: 'Consistent Income', desc: 'Weekly income from your God-given skills and efforts.', icon: ChartLineUp },
              { milestone: 'Stronger Discipline', desc: 'Accountability transforms your habits and character.', icon: ShieldCheck },
              { milestone: 'Deeper Brotherhood', desc: 'Relationships with men who sharpen you spiritually and professionally.', icon: Handshake },
              { milestone: 'Greater Impact', desc: 'Your success becomes a testimony and blessing to others.', icon: Target },
              { milestone: 'Kingdom Legacy', desc: 'Building wealth that serves your family for generations.', icon: Crown }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="card-premium p-6"
              >
                <item.icon size={28} className="text-[#C4A661] mb-4" weight="duotone" />
                <h3 className="text-lg font-bold mb-2 text-[#F7F7F5]">{item.milestone}</h3>
                <p className="text-[#A3A3A3] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-[#525252] mt-10 text-xs">
            Results vary based on effort and consistency. These are examples of what's possible when you execute with faith.
          </p>
        </div>
      </section>

      {/* What This Is NOT - Light Section */}
      <section className="section-light section-padding">
        <div className="container mx-auto max-w-4xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-overline mb-6">Let's Be Clear</p>
            <h2 className="text-heading-lg text-[#0A0A0A] mb-12">
              The Inner Circle Is <span className="text-red-600">NOT:</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                'A get-rich-quick scheme',
                'Success without discipline',
                'Passive income overnight',
                'For men who won\'t take action',
                'A shortcut around hard work',
                'For those who ignore God\'s principles'
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4 p-4 bg-red-50 border border-red-200"
                >
                  <X size={20} className="text-red-500 flex-shrink-0" weight="bold" />
                  <span className="text-[#525252]">{item}</span>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 p-8 bg-[#0A0A0A] border border-[#C4A661]/30">
              <p className="text-lg text-[#F7F7F5] leading-relaxed text-center">
                <Cross size={24} className="text-[#C4A661] inline-block mr-3 -mt-1" weight="fill" />
                "Lazy hands make for poverty, but diligent hands bring wealth."
                <span className="block text-sm text-[#A3A3A3] mt-2">— Proverbs 10:4</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For - Dark Section */}
      <section className="section-dark section-padding">
        <div className="container mx-auto max-w-4xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-overline mb-6">The Right Fit</p>
            <h2 className="text-heading-lg mb-12">
              This Is For You <span className="text-[#C4A661]">If:</span>
            </h2>

            <div className="space-y-3">
              {[
                'You\'re a Black man ready to build, not just learn',
                'You take your faith in Christ seriously',
                'You want accountability, not just encouragement',
                'You\'re willing to commit to consistent daily action',
                'You understand that building wealth takes time and discipline',
                'You want to be surrounded by men who pray and build together',
                'You\'re ready to invest in your growth',
                'You believe wealth is a tool for Kingdom impact'
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-4 bg-[#141414] border border-[#262626]"
                >
                  <Check size={22} className="text-[#C4A661] flex-shrink-0 mt-0.5" weight="bold" />
                  <span className="text-[#F7F7F5] text-lg">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section - Light */}
      <section id="join" className="section-light section-padding">
        <div className="container mx-auto max-w-4xl px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-16"
          >
            <p className="text-overline mb-6">Investment</p>
            <h2 className="text-heading-lg text-[#0A0A0A]">
              Join the <span className="text-[#C4A661]">Inner Circle</span>
            </h2>
            <p className="text-body-light max-w-xl mx-auto mt-4">
              Invest in your growth. Build with brothers. Honor God in your work.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Monthly Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onClick={() => setSelectedPlan('monthly')}
              className={`cursor-pointer p-8 transition-all ${
                selectedPlan === 'monthly' 
                  ? 'bg-[#0A0A0A] border-2 border-[#C4A661] text-[#F7F7F5]' 
                  : 'bg-[#F5F5F0] border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#C4A661]'
              }`}
              data-testid="monthly-plan"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">Monthly</h3>
                <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                  selectedPlan === 'monthly' ? 'border-[#C4A661] bg-[#C4A661]' : 'border-[#A3A3A3]'
                }`}>
                  {selectedPlan === 'monthly' && <Check size={14} className="text-[#0A0A0A]" weight="bold" />}
                </div>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-extrabold">$97</span>
                <span className={selectedPlan === 'monthly' ? 'text-[#A3A3A3]' : 'text-[#525252]'}>/month</span>
              </div>
              <ul className="space-y-3">
                {['Full access to all systems', 'Weekly accountability', 'Private brotherhood', 'Cancel anytime'].map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 ${selectedPlan === 'monthly' ? 'text-[#A3A3A3]' : 'text-[#525252]'}`}>
                    <Check size={16} className="text-[#C4A661]" weight="bold" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Lifetime Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              onClick={() => setSelectedPlan('lifetime')}
              className={`cursor-pointer p-8 transition-all relative ${
                selectedPlan === 'lifetime' 
                  ? 'bg-[#0A0A0A] border-2 border-[#C4A661] text-[#F7F7F5]' 
                  : 'bg-[#F5F5F0] border border-[#E5E5E5] text-[#0A0A0A] hover:border-[#C4A661]'
              }`}
              data-testid="lifetime-plan"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C4A661] text-[#0A0A0A] text-xs font-bold px-4 py-1 uppercase tracking-widest">
                Best Value
              </div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold">Lifetime</h3>
                <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                  selectedPlan === 'lifetime' ? 'border-[#C4A661] bg-[#C4A661]' : 'border-[#A3A3A3]'
                }`}>
                  {selectedPlan === 'lifetime' && <Check size={14} className="text-[#0A0A0A]" weight="bold" />}
                </div>
              </div>
              <div className="mb-6">
                <span className="text-5xl font-extrabold">$497</span>
                <span className={selectedPlan === 'lifetime' ? 'text-[#A3A3A3]' : 'text-[#525252]'}> one-time</span>
              </div>
              <ul className="space-y-3">
                {['Everything in Monthly', 'Lifetime access', 'All future updates', 'Priority support'].map((item, i) => (
                  <li key={i} className={`flex items-center gap-3 ${selectedPlan === 'lifetime' ? 'text-[#A3A3A3]' : 'text-[#525252]'}`}>
                    <Check size={16} className="text-[#C4A661]" weight="bold" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Join Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-md mx-auto"
          >
            {isAuthenticated ? (
              <div className="space-y-4 text-center">
                <p className="text-[#525252]">
                  Ready, <span className="text-[#C4A661] font-bold">{user?.name}</span>?
                </p>
                <Button 
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full btn-gold h-14 text-lg"
                  data-testid="checkout-btn"
                >
                  {loading ? 'Processing...' : `Checkout — ${selectedPlan === 'monthly' ? '$97/mo' : '$497 Lifetime'}`}
                  <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
                <p className="text-xs text-[#A3A3A3]">
                  Secure Stripe checkout. 30-day money-back guarantee.
                </p>
              </div>
            ) : (
              <form onSubmit={handleJoin} className="space-y-4">
                <Input
                  type="text"
                  placeholder="Your First Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white border-[#E5E5E5] text-[#0A0A0A] placeholder:text-[#A3A3A3] h-14 text-lg focus:border-[#C4A661]"
                  data-testid="name-input"
                />
                <Input
                  type="email"
                  placeholder="Your Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-[#E5E5E5] text-[#0A0A0A] placeholder:text-[#A3A3A3] h-14 text-lg focus:border-[#C4A661]"
                  data-testid="email-input"
                />
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gold h-14 text-lg"
                  data-testid="join-inner-circle-btn"
                >
                  {loading ? 'Processing...' : `Join Inner Circle — ${selectedPlan === 'monthly' ? '$97/mo' : '$497'}`}
                  <ArrowRight size={20} className="ml-2" weight="bold" />
                </Button>
                <p className="text-center text-xs text-[#A3A3A3]">
                  Sign up to continue to secure checkout. 30-day money-back guarantee.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Closing Section - Dark */}
      <section className="section-dark section-padding">
        <div className="container mx-auto max-w-3xl px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <BookOpen size={48} className="text-[#C4A661] mx-auto mb-8" weight="duotone" />
            
            <h2 className="text-heading-lg mb-8">
              The Environment You're In
              <br />
              <span className="text-[#C4A661] font-editorial italic font-normal">Shapes the Man You Become.</span>
            </h2>
            
            <p className="text-lg text-[#A3A3A3] mb-6 max-w-2xl mx-auto leading-relaxed">
              You can keep consuming content. Keep planning. Keep waiting for the "right time."
            </p>
            <p className="text-lg text-[#F7F7F5] mb-12 max-w-2xl mx-auto leading-relaxed">
              Or you can step into a brotherhood designed to help you <span className="text-[#C4A661]">execute</span>, 
              build wealth God's way, and leave a legacy that honors Him.
            </p>
            
            <Button asChild className="btn-gold h-14 px-10 text-lg">
              <a href="#join">
                Join the Inner Circle Now
                <ArrowRight size={20} className="ml-2" weight="bold" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[#262626]">
        <div className="container mx-auto max-w-5xl flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-2xl font-extrabold text-[#C4A661]">POWR</div>
          <p className="text-sm text-[#A3A3A3]">
            Purpose. Ownership. Wealth. Responsibility.
          </p>
          <div className="flex gap-6 text-sm text-[#A3A3A3]">
            <Link to="/" className="hover:text-[#F7F7F5] transition-colors">Home</Link>
            <Link to="/login" className="hover:text-[#F7F7F5] transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InnerCircleSalesPage;
